import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import { CalculatorConfigSchema, ProductSelectionSchema, packsForArea } from '@theme/custom-floor-calculator-data';

class FloorM2Calculator extends Component {
  requiredRefs = [
    'sqmInput',
    'packsInput',
    'totalPrice',
    'totalSqm',
    'sqmPerPack',
    'missingDataMessage',
    'requestedSqmProp',
    'packsProp',
    'sqmPerPackProp',
    'variantNotes',
  ];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #eventTarget = null;

  /** @type {HTMLElement | null} */
  #productForm = null;

  /** @type {HTMLFormElement | null} */
  #addToCartForm = null;

  /** @type {HTMLElement | null} */
  #quantitySelector = null;

  /** @type {HTMLInputElement | null} */
  #quantityInput = null;

  /** @type {number} */
  #sqmPerPack = 0;

  /** @type {number} */
  #variantPriceCents = 0;

  /** @type {number} */
  #quantityMin = 1;

  /** @type {number} */
  #quantityStep = 1;

  /** @type {number | null} */
  #quantityMax = null;

  /** @type {boolean} */
  #isSyncingQuantity = false;

  #selectionGeneration = 0;
  #selectionPending = false;
  #variantAvailable = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#resolveFormReferences();

    this.#readDataAttributes();
    this.#refreshQuantityElements();

    this.refs.sqmInput.addEventListener('input', this.#onSqmInput, { signal });
    this.refs.sqmInput.addEventListener('blur', this.#onSqmInput, { signal });
    this.refs.packsInput.addEventListener('input', this.#onPacksInput, { signal });
    this.refs.packsInput.addEventListener('blur', this.#onPacksInput, { signal });
    document.addEventListener('submit', this.#onDocumentSubmit, { signal, capture: true });

    if (this.#productForm) {
      this.#productForm.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, {
        signal,
      });
    }

    if (this.#eventTarget) {
      this.#eventTarget.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    }

    const quantityValue = this.#currentQuantity();
    this.#applyFromPacks(quantityValue, { syncQuantity: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #readDataAttributes() {
    try {
      const data = CalculatorConfigSchema.parse(this.dataset);
      this.#sqmPerPack = data.sqmPerPack;
      this.#variantPriceCents = data.priceCents;
      this.#variantAvailable = data.available;
    } catch {
      this.#sqmPerPack = 0;
      this.#variantPriceCents = 0;
      this.#variantAvailable = false;
    }

    const hasSqmData = this.#sqmPerPack > 0;

    this.refs.sqmInput.disabled = !hasSqmData || !this.#variantAvailable || this.#selectionPending;
    this.refs.packsInput.disabled = !hasSqmData || !this.#variantAvailable || this.#selectionPending;
    this.refs.missingDataMessage.hidden = hasSqmData || !this.#variantAvailable;
  }

  #resolveFormReferences() {
    const productFormId = this.dataset.productFormId;

    if (productFormId) {
      this.#addToCartForm = /** @type {HTMLFormElement | null} */ (document.getElementById(productFormId));
    }

    if (!this.#addToCartForm) {
      const root = this.#eventTarget || document;
      this.#addToCartForm = /** @type {HTMLFormElement | null} */ (
        root.querySelector('form[data-type="add-to-cart-form"]')
      );
    }

    this.#productForm =
      /** @type {HTMLElement | null} */ (this.#addToCartForm?.closest('product-form-component')) || null;
  }

  #refreshQuantityElements() {
    this.#resolveFormReferences();
    this.#quantitySelector = this.#productForm?.querySelector('quantity-selector-component') || null;
    this.#quantityInput =
      this.#addToCartForm?.querySelector('input[name="quantity"]') ||
      this.#productForm?.querySelector('input[name="quantity"]') ||
      this.#quantitySelector?.querySelector('input[name="quantity"]') ||
      null;

    const parsedMin = this.#parseInteger(this.#quantityInput?.min || '1');
    const parsedStep = this.#parseInteger(this.#quantityInput?.step || '1');
    const parsedMaxRaw = this.#quantityInput?.max;
    const parsedMax = parsedMaxRaw ? this.#parseInteger(parsedMaxRaw) : null;

    this.#quantityMin = Math.max(1, parsedMin);
    this.#quantityStep = Math.max(1, parsedStep);
    this.#quantityMax = parsedMax && parsedMax > 0 ? parsedMax : null;

    this.refs.packsInput.min = String(this.#quantityMin);
    this.refs.packsInput.step = String(this.#quantityStep);
    if (this.#quantityMax !== null) {
      this.refs.packsInput.max = String(this.#quantityMax);
    } else {
      this.refs.packsInput.removeAttribute('max');
    }
  }

  /** @param {Event} event */
  #onSqmInput = (event) => {
    this.#ensureQuantityElements();
    const requestedSqm = this.#parseNumber(this.refs.sqmInput.value);

    if (requestedSqm <= 0 || this.#sqmPerPack <= 0) {
      return;
    }

    const requestedPacks = packsForArea(requestedSqm, this.#sqmPerPack);
    this.#applyFromPacks(requestedPacks, {
      syncQuantity: true,
      requestedSqm,
      updateSqmInput: event.type === 'blur',
    });
  };

  #onPacksInput = () => {
    this.#ensureQuantityElements();
    const requestedPacks = this.#parseInteger(this.refs.packsInput.value);
    this.#applyFromPacks(requestedPacks, {
      syncQuantity: true,
      updateSqmInput: true,
    });
  };

  /** @param {Event} event */
  #onDocumentSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productFormId = this.dataset.productFormId;
    if (productFormId && form.id !== productFormId) return;
    if (!productFormId && form.dataset.type !== 'add-to-cart-form') return;

    this.#addToCartForm = form;
    this.#productForm = /** @type {HTMLElement | null} */ (form.closest('product-form-component'));
    this.#onFormSubmit();
  };

  #onFormSubmit = () => {
    if (this.#selectionPending || !this.#variantAvailable) return;
    this.#ensureQuantityElements();
    const packs = this.#normalizePacks(this.#parseInteger(this.refs.packsInput.value));
    this.#setQuantityValue(packs);
  };

  /** @param {Event & { detail?: { quantity?: number } }} event */
  #onQuantitySelectorUpdate = (event) => {
    if (this.#isSyncingQuantity) return;
    if (!this.#quantitySelector || event.target !== this.#quantitySelector) return;

    const quantity = this.#parseInteger(String(event.detail?.quantity ?? this.#currentQuantity()));
    this.#applyFromPacks(quantity, { syncQuantity: false, updateSqmInput: true });
  };

  /** @param {import('./standard-events').ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.target.closest('variant-picker')?.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.#selectionGeneration;
    const requestedSqm = this.#parseNumber(this.refs.sqmInput.value);
    const requestedPacks = this.#parseInteger(this.refs.packsInput.value);
    const previousArea = this.#sqmPerPack;
    this.#selectionPending = true;
    this.setAttribute('aria-busy', 'true');
    this.#readDataAttributes();
    this.refs.totalPrice.textContent = '…';
    try {
      const selection = ProductSelectionSchema.parse(await event.promise);
      if (generation !== this.#selectionGeneration || !this.isConnected) return;
      if (selection.productId !== this.dataset.productId || !(selection.html instanceof Document)) {
        throw new TypeError('Mismatched product selection');
      }
      const next = selection.html.querySelector(
        `custom-floor-m2-calculator-component[data-block-id="${CSS.escape(this.dataset.blockId)}"]`,
      );
      if (!(next instanceof HTMLElement)) throw new TypeError('Calculator missing from product response');
      const config = CalculatorConfigSchema.parse(next.dataset);
      if (selection.resource && config.priceCents !== selection.resource.price) throw new TypeError('Price mismatch');
      this.dataset.sqmPerPack = String(config.sqmPerPack);
      this.dataset.variantPriceCents = String(selection.resource?.price ?? 0);
      this.dataset.variantAvailable = String(selection.resource?.available === true);
      this.dataset.moneyFormat = config.moneyFormat;
      this.dataset.currencyCode = config.currencyCode;
      this.dataset.locale = config.locale;
      const notes = next.querySelector('[ref="variantNotes"]');
      this.refs.variantNotes.replaceChildren(...(notes ? [...notes.childNodes] : []));
      this.#selectionPending = false;
      this.#readDataAttributes();
      this.#refreshQuantityElements();
      const targetPacks =
        config.sqmPerPack !== previousArea && config.sqmPerPack > 0 && requestedSqm > 0
          ? packsForArea(requestedSqm, config.sqmPerPack)
          : requestedPacks;
      const packs = this.#normalizePacks(targetPacks);
      // Preserve the requested area unless new quantity rules force a different pack count.
      this.#applyFromPacks(packs, {
        syncQuantity: this.#variantAvailable,
        requestedSqm: packs === targetPacks ? requestedSqm : undefined,
      });
    } catch (error) {
      if (generation !== this.#selectionGeneration || !this.isConnected) return;
      this.#selectionPending = false;
      this.dataset.variantAvailable = 'false';
      this.#readDataAttributes();
      this.refs.totalPrice.textContent = this.dataset.unavailableLabel;
      if (error?.name !== 'AbortError') console.warn('[floor-calculator] Selection failed:', error);
    } finally {
      if (generation === this.#selectionGeneration) this.removeAttribute('aria-busy');
    }
  };

  /** @param {number} requestedPacks */
  #normalizePacks(requestedPacks) {
    let packs = Number.isFinite(requestedPacks) ? Math.ceil(requestedPacks) : this.#quantityMin;
    if (packs < this.#quantityMin) packs = this.#quantityMin;

    const offsetFromMin = packs - this.#quantityMin;
    const roundedOffset = Math.ceil(offsetFromMin / this.#quantityStep) * this.#quantityStep;
    packs = this.#quantityMin + roundedOffset;

    if (this.#quantityMax !== null) {
      packs = Math.min(this.#quantityMax, packs);
    }

    return packs;
  }

  #currentQuantity() {
    this.#ensureQuantityElements();
    const quantityFromForm = this.#parseInteger(this.#quantityInput?.value || '0');
    const quantityFromInput = this.#parseInteger(this.refs.packsInput.value || '0');

    if (quantityFromForm > 0) return this.#normalizePacks(quantityFromForm);
    if (quantityFromInput > 0) return this.#normalizePacks(quantityFromInput);
    return this.#quantityMin;
  }

  /**
   * @param {number} requestedPacks
   * @param {{ syncQuantity: boolean, requestedSqm?: number, updateSqmInput?: boolean }} options
   */
  #applyFromPacks(requestedPacks, options) {
    const packs = this.#normalizePacks(requestedPacks);

    if (this.#sqmPerPack <= 0) {
      this.refs.totalPrice.textContent = this.#variantAvailable ? '-' : this.dataset.unavailableLabel;
      this.refs.totalSqm.textContent = '-';
      this.refs.sqmPerPack.textContent = '-';
      return;
    }

    if (options.syncQuantity) {
      this.#syncQuantityInput(packs);
    }

    // Retain the package's decimal precision and remove multiplication noise (e.g. 3 × 2.675).
    const packagePrecision = this.#formatSqmInput(this.#sqmPerPack).split('.')[1]?.length || 0;
    const totalSqm = Number((packs * this.#sqmPerPack).toFixed(packagePrecision));
    const requestedSqm = options.requestedSqm && options.requestedSqm > 0 ? options.requestedSqm : totalSqm;
    const totalPriceCents = packs * this.#variantPriceCents;
    const unitLabel = this.dataset.unitLabel || '';

    this.refs.packsInput.value = String(packs);
    if (options.updateSqmInput !== false) {
      this.refs.sqmInput.value = this.#formatSqmInput(requestedSqm);
    }
    this.refs.totalPrice.textContent = this.#selectionPending
      ? '…'
      : this.#variantAvailable
        ? this.#formatPrice(totalPriceCents)
        : this.dataset.unavailableLabel;
    this.refs.totalSqm.textContent = `${this.#formatSqm(totalSqm)} ${unitLabel}`.trim();
    this.refs.sqmPerPack.textContent = `${this.#formatSqm(this.#sqmPerPack)} ${unitLabel}`.trim();

    this.refs.requestedSqmProp.value = this.#formatSqmInput(requestedSqm);
    this.refs.packsProp.value = String(packs);
    this.refs.sqmPerPackProp.value = this.#formatSqmInput(this.#sqmPerPack);
  }

  /** @param {number} packs */
  #syncQuantityInput(packs) {
    this.#ensureQuantityElements();
    if (!this.#quantityInput) return;

    this.#isSyncingQuantity = true;
    this.#setQuantityValue(packs);

    this.#isSyncingQuantity = false;
  }

  /** @param {number} packs */
  #setQuantityValue(packs) {
    if (!this.#quantityInput) return;

    if (this.#quantitySelector && typeof this.#quantitySelector.setValue === 'function') {
      this.#quantitySelector.setValue(String(packs));
      if (typeof this.#quantitySelector.onQuantityChange === 'function') {
        this.#quantitySelector.onQuantityChange();
      }
      if (typeof this.#quantitySelector.updateButtonStates === 'function') {
        this.#quantitySelector.updateButtonStates();
      }
    } else {
      this.#quantityInput.value = String(packs);
      this.#quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Always force native input value to guarantee submitted quantity.
    this.#quantityInput.value = String(packs);
  }

  #ensureQuantityElements() {
    if (this.#addToCartForm && this.#quantityInput) return;
    this.#refreshQuantityElements();
  }

  /** @param {number} cents */
  #formatPrice(cents) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currencyCode || 'SEK';

    return formatMoney(cents, moneyFormat, currency);
  }

  /** @param {number} value */
  #formatSqm(value) {
    const locale = this.dataset.locale || document.documentElement.lang || 'sv-SE';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 20,
    }).format(value);
  }

  /** @param {number} value */
  #formatSqmInput(value) {
    // HTML number inputs require "." as decimal separator regardless of locale.
    return new Intl.NumberFormat('en', {
      useGrouping: false,
      maximumFractionDigits: 20,
    }).format(value);
  }

  /** @param {string | undefined} value */
  #parseNumber(value) {
    if (!value) return 0;
    const normalized = value.replace(/\s/g, '').replace(',', '.');
    const number = Number.parseFloat(normalized);
    if (!Number.isFinite(number)) return 0;
    return number;
  }

  /** @param {string} value */
  #parseInteger(value) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? number : 0;
  }
}

if (!customElements.get('custom-floor-m2-calculator-component')) {
  customElements.define('custom-floor-m2-calculator-component', FloorM2Calculator);
}
